import { addDay, addMonth, diffDays, diffMonths, diffYears, parse } from '@formkit/tempo';
import { endOfMonth, format, isThisWeek, isThisYear, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export class DateHelper {
	static parseDate(date: string, format: string = '') {
		// if date contains /
		if (!date) return new Date(0, 0, 0);
		if (date.includes('/')) return parse(date, 'DD/MM/YYYY');

		if (format != '') return parse(date, format);
		return parse(date);
	}

	static diffBetweenDates(date1: string | Date, date2: string | Date, type: string = 'days') {
		if (typeof date1 === 'string') date1 = DateHelper.parseDate(date1);
		if (typeof date2 === 'string') date2 = DateHelper.parseDate(date2);

		switch (type) {
			case 'days':
				return diffDays(date1, date2);
			case 'months':
				return diffMonths(date1, date2);
			case 'years':
				return diffYears(date1, date2);
		}

		return -1;
	}

	static getWelcomeMessage() {
		const today = new Date();
		const curHr = today.getHours();

		if (curHr < 12) {
			return 'Bom dia';
		} else if (curHr < 18) {
			return 'Boa tarde';
		} else {
			return 'Boa noite';
		}
	}

	static toFormat(date: string | Date) {
		if (!date) return '-';

		let _date = new Date();
		if (typeof date === 'string') _date = parse(date);
		if (typeof date !== 'string') _date = date;

		return _date.toLocaleString('pt-BR');
	}

	static toMonthName(monthNumber: number | string) {
		const date = new Date();
		date.setMonth(Number(monthNumber) - 1);

		return date.toLocaleString('pt-BR', { month: 'long' });
	}

	static toShortFormat(date: string | Date | undefined): string {
		if (!date) return '-';

		const formatter = new Intl.DateTimeFormat('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});

		const isValidDate = (d: Date) => !isNaN(d.getTime());

		let parsed: Date | null = null;

		if (typeof date === 'string') {
			const datePart = date.split('T')[0];
			if (!datePart) return '-';
			const [year, month, day] = datePart.split('-').map(Number);
			if (!year || !month || !day) return '-';
			const raw = date.trim();
			if (!raw) return '-';

			// timestamp em string (segundos ou ms)
			if (/^\d+$/.test(raw)) {
				const n = Number(raw);
				const ms = raw.length === 10 ? n * 1000 : n;
				parsed = new Date(ms);
			}

			// ISO date (com ou sem timestamp): mantém só "YYYY-MM-DD" e constrói em horário local (sem shift)
			if (!parsed) {
				const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
				if (isoMatch) {
					const year = Number(isoMatch[1]);
					const month = Number(isoMatch[2]);
					const day = Number(isoMatch[3]);
					parsed = new Date(year, month - 1, day);
				}
			}

			// pt-BR "DD/MM/YYYY"
			if (!parsed) {
				const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
				if (brMatch) {
					const day = Number(brMatch[1]);
					const month = Number(brMatch[2]);
					const year = Number(brMatch[3]);
					parsed = new Date(year, month - 1, day);
				}
			}

			// fallback: tenta parsing nativo (pode sofrer shift dependendo do formato/timezone)
			if (!parsed) {
				parsed = new Date(raw);
			}
		} else {
			// Se veio um Date construído a partir de "YYYY-MM-DD" (meia-noite UTC),
			// normaliza usando componentes UTC pra evitar shift no toLocaleDateString.
			const isMidnightUTC =
				date.getUTCHours() === 0 &&
				date.getUTCMinutes() === 0 &&
				date.getUTCSeconds() === 0 &&
				date.getUTCMilliseconds() === 0;

			parsed = isMidnightUTC
				? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
				: date;
		}

		if (!parsed || !isValidDate(parsed)) return '-';

		return formatter.format(parsed);
	}

	static toShortDateTimeFormat(date: string | Date | undefined | null): string {
		if (!date) return '-';
		const parsed = typeof date === 'string' ? new Date(date) : date;
		if (isNaN(parsed.getTime())) return '-';
		return format(parsed, 'dd/MM/yyyy HH:mm');
	}

	static toDateMonthName(date: string | Date | undefined) {
		if (!date) return '-';

		let _date = new Date();
		if (typeof date === 'string') _date = new Date(date);
		if (typeof date !== 'string') _date = date;

		return _date
			.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
			.toLocaleUpperCase('pt-BR');
	}

	/**
	 * Return a time ago
	 *
	 * Data ausente ou malformada devolve travessão em vez de estourar. Não é preciosismo: o helper é
	 * chamado direto do render em listagem, card de busca e timeline, então um `updatedAt` nulo —
	 * registro importado, criado por rotina, nunca atualizado — derrubava a TELA INTEIRA, com o
	 * stack apontando para o naive-ui em vez de para a origem.
	 *
	 * @param date
	 * @returns
	 */
	static getTimeAgo(date: Date | string | null | undefined, hiddenAgo = false) {
		if (date == null) return '—';

		if (typeof date === 'string') date = new Date(date);

		if (Number.isNaN(date.getTime())) return '—';

		const now = new Date();
		const diff = Math.abs(now.getTime() - date.getTime());

		const minute = 60 * 1000;
		const hour = minute * 60;
		const day = hour * 24;
		const week = day * 7;
		const month = day * 30;
		const year = day * 365;

		if (diff < minute) {
			return Math.floor(diff / 1000) + ' segundos atrás';
		} else if (diff < hour) {
			return Math.floor(diff / minute) + ' minutos atrás';
		} else if (diff < day) {
			return Math.floor(diff / hour) + ' horas atrás';
		} else if (diff < week) {
			return Math.floor(diff / day) + ' dias atrás';
		} else if (diff < month) {
			return Math.floor(diff / week) + ' semanas atrás';
		} else if (diff < year) {
			return Math.floor(diff / month) + ' meses atrás';
		} else {
			return Math.floor(diff / year) + ' anos atrás';
		}
	}

	static formatMessageTime(date: Date | string): string {
		if (typeof date === 'string') date = new Date(date);

		if (!date) return '';

		// Invalid Date
		if (isNaN(date.getTime())) return '';

		if (isToday(date)) {
			return format(date, 'HH:mm', { locale: ptBR });
		} else if (isYesterday(date)) {
			return 'Ontem';
		} else if (isThisWeek(date)) {
			return format(date, 'EEEE', { locale: ptBR });
		} else if (isThisYear(date)) {
			return format(date, 'dd/MM', { locale: ptBR });
		} else {
			return format(date, 'dd/MM/yyyy', { locale: ptBR });
		}
	}

	static monthFromNumber(month: number) {
		return new Date(0, month).toLocaleString('pt-BR', { month: 'long' });
	}

	static firstDayOfMonth(month: number, year: number) {
		console.log(month);
		return new Date(year, month - 1, 1);
	}

	static lastDayOfMonth(month: number, year: number) {
		return endOfMonth(new Date(year, month - 1, 1));
	}

	static daysToExpire(date: string) {}

	static addDays(date: Date, days: number) {
		return addDay(date, days);
	}

	static addMonths(date: Date, months: number) {
		return addMonth(date, months);
	}

	static toDocumentFormat(date: string | Date | undefined): string {
		if (!date) return '-';

		return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
	}

	static isValidDate(date: string) {
		if (!date) return false;

		const parsed = new Date(date);

		return (
			!isNaN(parsed.getTime()) && parsed.getFullYear() > 1900 // evita 0001-01-01 e similares
		);
	}

	static formatMonthYear(dateString?: string | null): string {
		if (!dateString) return '';

		const match = dateString.match(/^(\d{4})-(\d{2})-/);
		if (!match) return '';

		const [, year, month] = match;
		return `${month}/${year}`;
	}

	static parseToLocalDate(dateInput: string | Date | undefined | null): Date {
		if (!dateInput) return new Date();
		if (dateInput instanceof Date) return dateInput;

		const dateStr = String(dateInput);
		const parts = dateStr.includes('T')
			? dateStr.split('T')[0].split('-')
			: dateStr.split(' ')[0].split('-');
		const [yyyyStr, mmStr, ddStr] = parts;
		if (yyyyStr && mmStr && ddStr) {
			return new Date(Number(yyyyStr), Number(mmStr) - 1, Number(ddStr));
		}

		const parsed = new Date(dateInput);
		return !isNaN(parsed.getTime()) ? parsed : new Date();
	}

	static formatIsoDate(dateInput: Date): string {
		const yyyy = dateInput.getFullYear();
		const mm = String(dateInput.getMonth() + 1).padStart(2, '0');
		const dd = String(dateInput.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	}

	static getYearFromIsoDate(isoDate: string): number | null {
		const match = isoDate?.match(/^(\d{4})-\d{2}-\d{2}$/);
		return match ? Number(match[1]) : null;
	}

	static isWeekend(isoDate: string): boolean {
		const date = DateHelper.parseToLocalDate(isoDate);
		const weekday = date.getDay();
		return weekday === 0 || weekday === 6;
	}

	static isBusinessDay(isoDate: string, holidays: Set<string>): boolean {
		if (!isoDate) return false;
		if (DateHelper.isWeekend(isoDate)) return false;
		return !holidays.has(isoDate);
	}
}
